import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, TextField, CircularProgress,
  FormControlLabel, Checkbox,
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import api from '../services/api';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirmed: () => void;
  openPositions: number;
  atRiskAmount: number;
}

export default function EmergencyStopDialog({ open, onClose, onConfirmed, openPositions, atRiskAmount }: Props) {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeManual, setIncludeManual] = useState(true);

  const isConfirmed = confirmText === 'STOP';

  const handleExecute = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.post('/api/v1/autonomous/emergency-stop', { include_manual: includeManual });
      setConfirmText('');
      setIncludeManual(true);
      onConfirmed();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to stop all trading');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setConfirmText('');
    setError(null);
    setIncludeManual(true);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { backgroundColor: '#FDF6EC', border: '2px solid #C0392B' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Warning sx={{ color: '#C0392B', fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#C0392B' }}>Stop All Trading</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This will immediately:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 2, '& li': { mb: 0.5 } }}>
          <li><Typography variant="body2">Sell {includeManual ? 'all' : 'autonomous'} open positions at market price</Typography></li>
          <li><Typography variant="body2">Deactivate all active strategies</Typography></li>
          <li><Typography variant="body2">Zero all strategy budgets</Typography></li>
          <li><Typography variant="body2">Stop all autonomous trading</Typography></li>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={includeManual}
              onChange={(e) => setIncludeManual(e.target.checked)}
              disabled={loading}
              sx={{ color: '#C0392B', '&.Mui-checked': { color: '#C0392B' } }}
            />
          }
          label={
            <Typography variant="body2">
              Also exit manual trades
            </Typography>
          }
          sx={{ mb: 2 }}
        />

        {openPositions > 0 && (
          <Box sx={{ bgcolor: 'rgba(192,57,43,0.08)', borderRadius: 1, p: 1.5, mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {openPositions} open position{openPositions !== 1 ? 's' : ''} worth ${atRiskAmount.toFixed(2)} will be sold at current market price.
            </Typography>
          </Box>
        )}

        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          Type <span style={{ color: '#C0392B', fontFamily: 'monospace' }}>STOP</span> to confirm:
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="Type STOP"
          disabled={loading}
          autoFocus
          sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'monospace', fontWeight: 700 } }}
        />

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleExecute}
          disabled={!isConfirmed || loading}
          variant="contained"
          sx={{
            backgroundColor: '#C0392B',
            fontWeight: 700,
            '&:hover': { backgroundColor: '#A93226' },
            '&.Mui-disabled': { backgroundColor: 'rgba(192,57,43,0.3)', color: 'rgba(255,255,255,0.6)' },
          }}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {loading ? 'Stopping...' : 'STOP ALL'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
